import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../AppContext";
import serversService from "./services/serversService";
import databaseSearchActions from "./services/databaseSearchActions";
import serverSettingsService from "./services/serverSettingsService";
import licenseService from "./services/licenseService";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import Menubar from "../Menubar";
import CreateNewServerDialog from "./CreateNewServerDialog";
import {
  convertTimestampNoTime,
  convertTimestampWithTime,
} from "../utils/convertUTC";
import ServiceProviderComponent from "./components/ServiceProviderComponent";

const ServersPage = () => {
  const { isAdmin } = useAppContext();
  const navigate = useNavigate();
  const [servers, setServers] = useState([]);
  const [serverDetails, setServerDetails] = useState({
    data: {},
    isFetching: false,
  });
  const [showNewServerDialog, setShowNewServerDialog] = useState(false);
  const [showAccordion, setShowAccordion] = useState(null);
  const [databaseSearchValue, setDatabaseSearchValue] = useState("");
  const [databaseFiltering, setDatabaseFiltering] = useState(false);
  const [databaseSearchResults, setDatabaseSearchResults] = useState([]);

  const [searchTermUsername, setSearchTermUsername] = useState("");
  const [searchTermFriendlyName, setSearchTermFriendlyName] = useState("");
  const [searchTermServerName, setSearchTermServerName] = useState("");
  const [searchTermServerDNS, setSearchTermServerDNS] = useState("");
  const [searchTermIpAddress, setSearchTermIpAddress] = useState("");
  const [searchTermLicenseValid, setSearchTermLicenseValid] = useState("");
  const [searchTermCreated, setSearchTermCreated] = useState("");
  const [searchTermUpdated, setSearchTermUpdated] = useState("");
  const [searchTermLastSeen, setSearchTermLastSeen] = useState("");

  const [toastOpen, setToastOpen] = useState(false);
  const [serverResponse, setServerResponse] = useState({});

  const [sortBy, setSortBy] = useState("severity");
  const [isSortAsc, setIsSortAsc] = useState(false); // true=asc, false=desc

  const oneMonthFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const threeMonthsFromNow = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);

  const twoMonthsAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  useEffect(() => {
    if (!servers || servers.length === 0) {
      fetchServers();
    }
  }, []);

  useEffect(() => {
    sortAndSetServers();
  }, [sortBy, isSortAsc]);

  const sortAndSetServers = (serverList) => {
    if (!serverList) {
      serverList = servers;
    }

    const sortedServers = serverList.slice().sort((item1, item2) => {
      const val1 = item1[sortBy];
      const val2 = item2[sortBy];

      const isNil1 = val1 === undefined || val1 === null || val1 === "";
      const isNil2 = val2 === undefined || val2 === null || val2 === "";

      // Handle null/undefined values
      if (isNil1 && isNil2) return 0;
      if (isNil1) return isSortAsc ? 1 : -1;
      if (isNil2) return isSortAsc ? -1 : 1;

      // Handle strings
      if (typeof val1 === "string" && typeof val2 === "string") {
        return isSortAsc ? val1.localeCompare(val2) : val2.localeCompare(val1);
      }

      // Handle numbers or other comparable types
      if (val1 < val2) return isSortAsc ? -1 : 1;
      if (val1 > val2) return isSortAsc ? 1 : -1;
      return 0;
    });

    setServers(sortedServers);
  };

  const fetchServers = async () => {
    const result = await serversService.getServers();
    if (result.success) {
      const serversWithSeverity = mapSeverityToServers(result.servers);
      sortAndSetServers(serversWithSeverity);
    } else if (result.loggedOut) {
      // navigate("/login");
    }
  };

  const handleSearch = async () => {
    if (databaseSearchValue === "") {
      setDatabaseFiltering(false);
      return;
    }

    const { success, data } = await databaseSearchActions.SearchSites(
      databaseSearchValue
    );
    if (success) {
      setDatabaseSearchResults(data);
      setDatabaseFiltering(true);
    } else if (result.loggedOut) {
      navigate("/login");
    }
  };

  const handleDatabaseFiltering = (server) => {
    const uuids = databaseSearchResults?.map((site) => site.username);
    return uuids?.includes(server.username);
  };

  const mapSeverityToServers = (servers) => {
    return servers.map((server) => {
      let severity = 0;

      const licenseValidSeverity = getLicenseValidColor(server.licenseValid);
      const lastSeenSeverity = getLastSeenColor(server.last_connected);

      if (licenseValidSeverity === "red" || lastSeenSeverity === "red") {
        severity = 2; // High severity
      } else if (
        licenseValidSeverity === "orange" ||
        lastSeenSeverity === "orange"
      ) {
        severity = 1; // Medium severity
      }

      return { ...server, severity };
    });
  };

  const onRefreshClicked = async () => {
    await fetchServers();
  };

  const onCreateNewServerClicked = () => {
    setShowNewServerDialog(true);
  };

  const onNewServerCreate = async (friendlyName) => {
    setShowNewServerDialog(false);
    const response = await serversService.createServer(friendlyName);
    setToastOpen(true);
    setServerResponse(response);

    if (response.success) {
      await fetchServers();
    }
  };

  const setSortAction = (column) => {
    if (sortBy === column) {
      setSortBy(column);
      setIsSortAsc(!isSortAsc);
    } else {
      setSortBy(column);
      setIsSortAsc(true);
    }
  };

  const setShowAccordionAction = (index) => {
    if (showAccordion === index) {
      setShowAccordion(null);
    } else {
      setShowAccordion(index);
      fetchServerDetails(index);
    }
  };

  const fetchServerDetails = async (id) => {
    setServerDetails({ data: {}, isFetching: true });

    const settings = await serverSettingsService.fetchServerSettings(id);
    const { licenses, srvproviders } = await licenseService.fetchLicenses(id);

    setServerDetails({ settings, licenses, srvproviders, isFetching: false });
  };

  const getLicenseValidColor = (licenseValid) => {
    const licenseDate = new Date(licenseValid);
    let color = "inherit";

    if (licenseDate <= oneMonthFromNow) {
      color = "red";
    } else if (licenseDate <= threeMonthsFromNow) {
      color = "orange";
    }

    return color;
  };

  const getLastSeenColor = (lastSeen) => {
    if (!lastSeen) {
      return "inherit";
    }

    const lastSeenDate = new Date(lastSeen);
    let color = "inherit";
    if (lastSeenDate > oneWeekAgo) {
      color = "inherit";
    } else if (lastSeenDate > twoMonthsAgo) {
      color = "orange";
    } else {
      color = "red";
    }

    return color;
  };

  return (
    <>
      {showNewServerDialog && (
        <CreateNewServerDialog
          onCancel={() => setShowNewServerDialog(false)}
          onCreate={(friendlyName) => onNewServerCreate(friendlyName)}
        ></CreateNewServerDialog>
      )}

      <Menubar />
      <div style={{ marginBop: "1rem", marginBottom: "1rem" }} />

      <Button onClick={onRefreshClicked}>Refresh Data</Button>
      <Button style={{ marginLeft: "2rem" }} onClick={onCreateNewServerClicked}>
        Create New Server
      </Button>

      <div style={{ float: "right" }}>
        <IconButton
          title="Search Sites by Name"
          onClick={() => {
            setDatabaseFiltering(false);
          }}
        >
          {databaseFiltering ? <CloseIcon /> : <InfoIcon />}
        </IconButton>
        <TextField
          id="search-database"
          size="small"
          label="Database Search"
          onChange={(e) => setDatabaseSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
        />
        <IconButton onClick={handleSearch}>
          <SearchIcon />
        </IconButton>
      </div>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>
                <TextField
                  id="search-username"
                  label="Filter ID"
                  variant="outlined"
                  onChange={(e) => setSearchTermUsername(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  id="search-friendly-name"
                  label="Filter Friendly Name"
                  variant="outlined"
                  onChange={(e) => setSearchTermFriendlyName(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  id="search-server-name"
                  label="Filter Server Name"
                  variant="outlined"
                  onChange={(e) => setSearchTermServerName(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  id="search-server-dns"
                  label="Filter Server DNS"
                  variant="outlined"
                  onChange={(e) => setSearchTermServerDNS(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  id="search-ip-address"
                  label="Filter IP Address"
                  variant="outlined"
                  onChange={(e) => setSearchTermIpAddress(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  id="search-license-valid"
                  label="Filter License Valid"
                  variant="outlined"
                  onChange={(e) => setSearchTermLicenseValid(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  id="search-created"
                  label="Filter Created"
                  variant="outlined"
                  onChange={(e) => setSearchTermCreated(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  id="search-updated"
                  label="Filter Updated"
                  variant="outlined"
                  onChange={(e) => setSearchTermUpdated(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  id="search-last-seen"
                  label="Filter Last Seen"
                  variant="outlined"
                  onChange={(e) => setSearchTermLastSeen(e.target.value)}
                  style={{ width: "100%" }}
                />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell></TableCell>
              <TableCell onClick={() => setSortAction("severity")}>
                {/* Server Issue Severity */}
                <Button title="Server Issue Severity">
                  <WarningIcon />
                  {sortBy === "severity" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("username")}>
                <Button>
                  Server Id {sortBy === "username" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("friendly_name")}>
                <Button>
                  Friendly Name
                  {sortBy === "friendly_name" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("srvname")}>
                <Button>
                  Server Name {sortBy === "srvname" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("server_dns")}>
                <Button>
                  Server DNS
                  {sortBy === "server_dns" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("ip_address")}>
                <Button>
                  IP Address
                  {sortBy === "ip_address" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("licenseValid")}>
                <Button>
                  License Valid
                  {sortBy === "licenseValid" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("createdAt")}>
                <Button>
                  Created {sortBy === "createdAt" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("updatedAt")}>
                <Button>
                  Updated {sortBy === "updatedAt" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell onClick={() => setSortAction("last_connected")}>
                <Button>
                  Last Seen
                  {sortBy === "last_connected" && (isSortAsc ? "↑" : "↓")}
                </Button>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servers
              ?.filter((server) => {
                if (databaseFiltering) {
                  return handleDatabaseFiltering(server);
                }

                return (
                  (searchTermUsername === "" ||
                    server.username
                      ?.toLowerCase()
                      .includes(searchTermUsername)) &&
                  (searchTermFriendlyName === "" ||
                    server.friendly_name
                      ?.toLowerCase()
                      .includes(searchTermFriendlyName)) &&
                  (searchTermServerName === "" ||
                    server.srvname
                      ?.toLowerCase()
                      .includes(searchTermServerName)) &&
                  (searchTermServerDNS === "" ||
                    server.server_dns
                      ?.toLowerCase()
                      .includes(searchTermServerDNS)) &&
                  (searchTermIpAddress === "" ||
                    server.ip_address
                      ?.toLowerCase()
                      .includes(searchTermIpAddress)) &&
                  (searchTermLicenseValid === "" ||
                    server.licenseValid
                      ?.toLowerCase()
                      .includes(searchTermLicenseValid)) &&
                  (searchTermCreated === "" ||
                    convertTimestampNoTime(server.createdAt)
                      ?.toLowerCase()
                      .includes(searchTermCreated)) &&
                  (searchTermUpdated === "" ||
                    convertTimestampNoTime(server.updatedAt)
                      ?.toLowerCase()
                      .includes(searchTermUpdated)) &&
                  (searchTermLastSeen === "" ||
                    convertTimestampNoTime(server.last_connected)
                      ?.toLowerCase()
                      .includes(searchTermLastSeen))
                );
              })
              .map((server, i) => {
                return (
                  <Fragment key={i}>
                    <TableRow key={i}>
                      <TableCell
                        title="Show Details"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowAccordionAction(server.username)}
                      >
                        <ExpandMoreIcon
                          style={
                            showAccordion === server.username
                              ? { transform: "rotate(180deg)" }
                              : { transform: "rotate(0deg)" }
                          }
                        />
                      </TableCell>
                      <TableCell
                        title={"Issue Severity Level: " + server.severity}
                        style={{ textAlign: "center" }}
                      >
                        {/* {server.severity} */}
                        {server.severity > 0 && (
                          <WarningIcon
                            style={{
                              color: server.severity === 1 ? "orange" : "red",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell title={server.username}>
                        {server.username}
                      </TableCell>
                      <TableCell title={server.friendly_name}>
                        {server.friendly_name}
                      </TableCell>
                      <TableCell title={server.srvname}>
                        {server.srvname}
                      </TableCell>
                      <TableCell title={server.server_dns}>
                        {server.server_dns}
                      </TableCell>
                      <TableCell title={server.ip_address}>
                        {server.ip_address}
                      </TableCell>
                      <TableCell
                        style={{
                          color: getLicenseValidColor(server.licenseValid),
                        }}
                        title={convertTimestampWithTime(server.licenseValid)}
                      >
                        {convertTimestampNoTime(server.licenseValid)}
                      </TableCell>

                      <TableCell
                        title={convertTimestampWithTime(server.createdAt)}
                      >
                        {convertTimestampNoTime(server.createdAt)}
                      </TableCell>

                      <TableCell
                        title={convertTimestampWithTime(server.updatedAt)}
                      >
                        {convertTimestampNoTime(server.updatedAt)}
                      </TableCell>

                      <TableCell
                        style={{
                          color: getLastSeenColor(server.last_connected),
                        }}
                        title={convertTimestampWithTime(server.last_connected)}
                      >
                        {convertTimestampNoTime(server.last_connected)}
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SettingsIcon />}
                          onClick={() =>
                            navigate(`/serverdetails/${server.username}`)
                          }
                        >
                          Settings
                        </Button>
                      </TableCell>
                    </TableRow>
                    {showAccordion === server.username && (
                      <TableRow key={i + 1}>
                        <TableCell></TableCell>
                        <TableCell colSpan={12}>
                          <ServiceProviderComponent
                            providers={serverDetails.srvproviders}
                            isFetching={serverDetails.isFetching}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ServersPage;
